import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { Field, Form } from '@components/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUsersPasswordResetConfirm } from '@hooks/users';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import handleRequestError from '@utils/handleRequestError';

const initialValues = {
  password: '',
  confirmPassword: '',
};

const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    // At least 8 chars, one uppercase, one lowercase, one digit and one special char
    .matches(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/, {
      message:
        'Password must be at least 8 characters long and include uppercase, lowercase, number and special character',
    }),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], "Passwords don't match"),
});

export function ResetPassword() {
  const [passwordSent, setPasswordSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] = useState(false);
  const methods = useForm({
    resolver: yupResolver(ResetPasswordSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  });

  const { formState } = methods;
  const passwordValue = methods.watch('password', '');
  const confirmValue = methods.watch('confirmPassword', '');

  const location = useLocation();

  const resetToken = useMemo(() => {
    const tokenFromQuery = new URLSearchParams(location.search).get('token'); //?token=...
    return tokenFromQuery || null;
  }, [location.search]);

  const { confirmPasswordReset } = useUsersPasswordResetConfirm({
    onSuccess: () => {
      setPasswordSent(true);
    },
    onError: (error) => {
      // A password validation error belongs next to the field, and only there.
      // Anything else — every 5xx included — goes through handleRequestError so
      // it is both toasted and reported.
      const passwordErrors = error?.response?.data?.password;
      if (passwordErrors?.length) {
        methods.setError('root.server', { message: passwordErrors.join('\n') });
        return;
      }
      handleRequestError(error);
    },
  });

  const onSubmit = (values) => {
    confirmPasswordReset({
      password: values?.password || '',
      token: resetToken,
    });
  };

  // Manual validity checks so the submit button stays disabled until
  // the password meets all requirements and the confirmation matches.
  const passwordValid =
    /.{8,}/.test(passwordValue) &&
    /[A-Z]/.test(passwordValue) &&
    /[a-z]/.test(passwordValue) &&
    /[0-9]/.test(passwordValue) &&
    /[^A-Za-z0-9]/.test(passwordValue);

  const confirmValid = confirmValue !== '' && confirmValue === passwordValue;
  return (
    <Box sx={{ textAlign: 'center', mb: 5, minWidth: { xs: '100%', sm: 400 }, mx: 'auto' }}>
      {!passwordSent ? (
        <>
          <Stack sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              component="h3"
              sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
              variant="h5"
            >
              Reset your password
            </Typography>
            <Box
              sx={(theme) => ({
                color: 'text.secondary',
                fontWeight: theme.typography.fontWeightBold,
              })}
            >
              Enter a new password and confirm it
            </Box>
          </Stack>

          <Form methods={methods} onSubmit={onSubmit}>
            {formState?.errors?.root?.server && (
              <Box sx={{ mb: 3, bgcolor: 'error.light', p: 2, borderRadius: 1 }}>
                <Box
                  sx={(theme) => ({
                    fontWeight: theme.typography.fontWeightBold,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'left',
                  })}
                >
                  {formState.errors.root.server.message}
                </Box>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Field.Text
                hideErrorMessage
                name="password"
                placeholder="New password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ width: '100%' }}
                type={showPassword ? 'text' : 'password'}
              />
              <Box sx={{ textAlign: 'left', mt: 1 }}>
                <Typography sx={{ mb: 0.5, fontWeight: 'bold' }} variant="body2">
                  {passwordValue ? 'Password must:' : null}
                </Typography>
                <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {!/.{8,}/.test(passwordValue) && (
                    <Typography component="li" sx={{ color: 'error.main' }} variant="caption">
                      ✕ Minimum 8 characters
                    </Typography>
                  )}

                  {!/[A-Z]/.test(passwordValue) && (
                    <Typography component="li" sx={{ color: 'error.main' }} variant="caption">
                      ✕ Include at least one capital letter
                    </Typography>
                  )}

                  {!/[a-z]/.test(passwordValue) && (
                    <Typography component="li" sx={{ color: 'error.main' }} variant="caption">
                      ✕ Include at least one lowercase letter
                    </Typography>
                  )}

                  {!/[0-9]/.test(passwordValue) && (
                    <Typography component="li" sx={{ color: 'error.main' }} variant="caption">
                      ✕ Include at least one number
                    </Typography>
                  )}

                  {!/[^A-Za-z0-9]/.test(passwordValue) && (
                    <Typography component="li" sx={{ color: 'error.main' }} variant="caption">
                      ✕ Include at least one special character
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Field.Text
                name="confirmPassword"
                placeholder="Confirm password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowConfirmationPassword((prev) => !prev)}
                        >
                          {showConfirmationPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ width: '100%' }}
                type={showConfirmationPassword ? 'text' : 'password'}
              />
            </Box>

            <Stack alignItems="center" direction="row" justifyContent="center">
              <Button
                disabled={!passwordValid || !confirmValid || formState.isSubmitting}
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightBold,
                  mx: 2,
                  px: 9,
                  py: 1.5,
                })}
                type="submit"
                variant="contained"
              >
                {formState.isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Stack>
          </Form>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography
            component="h3"
            sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
            variant="h5"
          >
            Your password has been reset successfully!
          </Typography>
          <Box
            sx={(theme) => ({
              color: 'text.secondary',
              fontWeight: theme.typography.fontWeightBold,
            })}
          >
            You can now log in with your new password.
          </Box>
          <Button
            component={Link}
            sx={(theme) => ({
              mt: 3,
              backgroundColor: 'primary.light',
              fontWeight: theme.typography.fontWeightBold,
              px: 4,
              minWidth: 140,
            })}
            to="/admin/auth/login"
            variant="contained"
          >
            Go to Login
          </Button>
        </Box>
      )}
    </Box>
  );
}
